import { useState, useEffect, useRef } from 'react';
import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RiTelegramFill, RiDiscordFill } from 'react-icons/ri';

const HelpFloatingButton = () => {
  const { t } = useTranslation('common');
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);
  const [showFloatingCircles, setShowFloatingCircles] = useState(false);
  const desktopContainerRef = useRef<HTMLDivElement>(null);

  const handleHelpButtonClick = () => {
    setShowFloatingCircles(prev => !prev);
  };

  const handleTelegramClick = () => {
    window.open('https://t.me/espotz/2', '_blank', 'noopener,noreferrer');
    setShowHelpText(false);
    setShowFloatingCircles(false);
  };

  const handleDiscordClick = () => {
    window.open(
      'https://discord.gg/pZAcHNaFY8',
      '_blank',
      'noopener,noreferrer',
    );
    setShowHelpText(false);
    setShowFloatingCircles(false);
  };

  // Show help text only on first load
  useEffect(() => {
    // Initial highlight after 3 seconds on mount (first load only)
    const initialTimeout = setTimeout(() => {
      setIsHighlighted(true);
      setShowHelpText(true);

      // Hide highlight after 3 seconds
      setTimeout(() => {
        setIsHighlighted(false);
      }, 3000);

      // Hide help text after 5 seconds
      setTimeout(() => {
        setShowHelpText(false);
      }, 5000);
    }, 3000);

    return () => {
      clearTimeout(initialTimeout);
    };
  }, []);

  // Close floating circles when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideDesktop =
        desktopContainerRef.current &&
        !desktopContainerRef.current.contains(target);

      if (showFloatingCircles && isOutsideDesktop) {
        setShowFloatingCircles(false);
      }
    };

    if (showFloatingCircles) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFloatingCircles]);

  const helpText = t('help.support') || 'Need Help?';

  return (
    <>
      {/* Desktop Version - Hidden on mobile. The wrapper lets clicks through: while the label is
          hidden its empty box still spans ~250px and covered whatever sat under it (e.g. a link
          in the /login card). Only the label (when shown) and the buttons take clicks. */}
      <div
        ref={desktopContainerRef}
        className='hidden md:flex fixed end-0 top-[60%] z-50 items-center select-none -translate-y-1/2 transform pointer-events-none'
      >
        {/* Sliding Help Text */}
        <div
          className={`me-2 px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-lg shadow-lg whitespace-nowrap transition-all duration-500 ease-in-out ${
            showHelpText
              ? 'translate-x-0 opacity-100 pointer-events-auto'
              : 'translate-x-full opacity-0 pointer-events-none'
          }`}
        >
          {helpText}
        </div>

        {/* Floating Circles */}
        {showFloatingCircles && (
          <div className='flex items-center gap-3 me-3 pointer-events-auto'>
            {/* Telegram Circle */}
            <button
              onClick={handleTelegramClick}
              className='flex items-center justify-center w-12 h-12 bg-sky-500 hover:bg-sky-600 text-white rounded-full shadow-lg hover:shadow-xl hover:shadow-sky-500/50 hover:scale-110 active:scale-95 transition-all duration-300 animate-in fade-in zoom-in-95 slide-in-from-right-2 rtl:slide-in-from-left-2'
              aria-label='Telegram Support'
            >
              <RiTelegramFill className='w-6 h-6' />
            </button>
            {/* Discord Circle */}
            <button
              onClick={handleDiscordClick}
              className='flex items-center justify-center w-12 h-12 bg-indigo-500 hover:bg-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl hover:shadow-indigo-500/50 hover:scale-110 active:scale-95 transition-all duration-300 animate-in fade-in zoom-in-95 slide-in-from-right-2 rtl:slide-in-from-left-2 delay-75'
              aria-label='Discord Support'
            >
              <RiDiscordFill className='w-6 h-6' />
            </button>
          </div>
        )}

        {/* Help Button */}
        <button
          onClick={handleHelpButtonClick}
          onMouseEnter={() => setShowHelpText(true)}
          onMouseLeave={() => {
            // Only hide if not in initial highlight phase
            if (!isHighlighted) {
              setShowHelpText(false);
            }
          }}
          className={`pointer-events-auto flex items-center justify-center w-8 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-l-lg shadow-lg hover:shadow-xl hover:shadow-blue-500/50 hover:scale-110 active:scale-95 transition-all duration-300 group ${
            isHighlighted
              ? 'animate-pulse-glow-blue ring-2 ring-blue-400 ring-offset-2'
              : ''
          } ${showFloatingCircles ? 'bg-blue-600' : ''}`}
          aria-label={t('help.support') || 'Get Help'}
          title={t('help.support') || 'Get Help - Contact Support'}
        >
          <HelpCircle
            className={`w-6 h-6 group-hover:rotate-12 transition-transform duration-300 ${
              isHighlighted ? 'scale-110' : ''
            } ${showFloatingCircles ? 'rotate-90' : ''}`}
          />
        </button>
      </div>
    </>
  );
};

export default HelpFloatingButton;
